import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Drawer, Input, Space, Typography } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { FC, useState } from 'react';
import styled from 'styled-components';

import { programsApi } from 'src/features/programsV2/api/programsApi';
import {
  showErrorModal,
  showInfoModal,
  showSuccessModal,
} from 'src/features/programsV2/utils/modalHelpers';

import { CreateInstructorAvatar } from './CreateInstructorAvatar';

const { Text } = Typography;
const { TextArea } = Input;

interface CreateInstructorProps {
  buttonStyle?: React.CSSProperties;
}

export const CreateInstructor: FC<CreateInstructorProps> = ({
  buttonStyle = {},
}) => {
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [avatar, setAvatar] = useState<RcFile | null>(null);
  const [avatarKey, setAvatarKey] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const isDisabled = !name || !description || !avatar;

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);

    setTimeout(() => {
      setName('');
      setDescription('');
      setAvatar(null);
      setAvatarKey(avatarKey + 1);
    }, 150);
  };

  const handleFinish = () => {
    queryClient.resetQueries({ queryKey: ['initial-step-options'] });
    onClose();
  };

  const { mutateAsync: createInstructor, isPending: isCreating } = useMutation({
    mutationFn: (instructorData: FormData) =>
      programsApi.createInstructor(instructorData),
  });

  const handleCreateInstructor = async () => {
    try {
      if (!avatar) {
        showInfoModal({
          title: 'Avatar necessário',
          content: 'Preencha o avatar do treinador e tente novamente',
        });
        return;
      }

      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('image', avatar);

      await createInstructor(formData);

      showSuccessModal({
        title: 'Sucesso',
        content: 'Treinador cadastrado com sucesso!',
        afterClose: handleFinish,
      });
    } catch (err) {
      showErrorModal({
        title: 'Tivemos um problema...',
        content: 'Não foi possível cadastrar o treinador, tente novamente.',
      });
    }
  };

  return (
    <>
      <Button
        type='link'
        icon={<PlusOutlined />}
        style={{ marginTop: '8px', marginLeft: 'auto', ...buttonStyle }}
        onClick={showDrawer}
      >
        Adicionar treinador(a)
      </Button>

      <Drawer
        title='Adicionar treinador(a)'
        width={700}
        onClose={onClose}
        open={open}
        styles={{
          header: {
            padding: '24px 16px',
            backgroundColor: '#141414',
          },
          body: {
            position: 'relative',
            padding: '18px 16px 80px',
            backgroundColor: '#141414',
          }
        }}
      >
        <CreateInstructorAvatar key={avatarKey} onChange={setAvatar} />

        <Text
          type='secondary'
          style={{ display: 'inline-block', color: '#FFFFFFD9' }}
        >
          Nome da bio
        </Text>
        <Input
          placeholder='Nome do(a) treinador(a)'
          value={name}
          onChange={event => setName(event.target.value)}
          maxLength={300}
          style={{ marginBottom: '16px' }}
        />

        <Text
          type='secondary'
          style={{
            display: 'inline-block',
            marginBottom: '4px',
            color: '#FFFFFFD9',
          }}
        >
          Descrição*
        </Text>
        <TextArea
          placeholder='Descrição breve do(a) treinador(a)'
          value={description}
          onChange={event => setDescription(event.target.value)}
          maxLength={300}
          showCount
          rows={5}
          style={{ marginBottom: '4px' }}
        />

        <DrawerFooter>
          <Divider style={{ margin: '0' }} />

          <Space
            style={{
              width: '100%',
              padding: '24px 16px',
              justifyContent: 'flex-end',
            }}
          >
            <Button onClick={onClose} style={{ borderRadius: '6px' }}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateInstructor}
              type='primary'
              loading={isCreating}
              disabled={isDisabled}
              style={{ borderRadius: '6px' }}
            >
              Salvar
            </Button>
          </Space>
        </DrawerFooter>
      </Drawer>
    </>
  );
};

const DrawerFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;
